// SendDocumentDialog — share a client document with one or more portal users
// (family principal + external advisers scoped to this client). Generates a
// fresh 14-day magic link per recipient and emails them via Resend.
import { useEffect, useState } from 'react';
import { Send, Loader2, Mail, X, UserCheck, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  family_principal: 'Family Principal',
  external_adviser: 'External Adviser',
};

export default function SendDocumentDialog({ document: doc, clientId, onClose, onSent }) {
  const { token } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/clients/${clientId}/document-recipients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load recipients');
        const body = await res.json();
        if (!cancelled) setRecipients(body.items || []);
      } catch (e) {
        if (!cancelled) toast.error('Could not load portal recipients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, clientId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one recipient');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/clients/${clientId}/documents/${doc.id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientIds: [...selected], note: note.trim() || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.detail || 'Could not send document');
        return;
      }
      const okCount = body.sent?.length || 0;
      if (okCount === 0) {
        toast.error('No emails were sent. Check the recipient configuration.');
      } else {
        toast.success(`"${body.documentName}" sent to ${okCount} recipient${okCount === 1 ? '' : 's'}`);
        onSent?.(body);
        onClose();
      }
    } catch (e) {
      toast.error('Could not send document');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="send-doc-dialog"
    >
      <div
        className="bg-white rounded-2xl border border-[#E6E0D9] w-full max-w-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-[#F0EAE3] flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A3B32]/10 flex items-center justify-center">
              <Send className="w-4 h-4 text-[#4A3B32]" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-[#2C2420] leading-tight">Send to client</h3>
              <p className="text-xs text-[#8C8078] mt-1 max-w-sm truncate">{doc?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#8C8078] hover:text-[#2C2420]" data-testid="send-doc-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Recipients */}
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-[#2C2420]">Recipients</Label>
            {loading ? (
              <div className="py-6 flex items-center justify-center text-[#8C8078]">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading portal users…
              </div>
            ) : recipients.length === 0 ? (
              <div className="mt-2 rounded-lg border border-dashed border-[#E6E0D9] p-4 text-sm text-[#5D524D]">
                No portal users for this client yet.{' '}
                <span className="text-[#8C8078]">Invite a Family Principal or External Adviser from the Adviser Hub first.</span>
              </div>
            ) : (
              <ul className="mt-2 space-y-1.5 max-h-60 overflow-y-auto" data-testid="send-doc-recipients">
                {recipients.map((r) => {
                  const checked = selected.has(r.id);
                  const Icon = r.portalRole === 'family_principal' ? UserCheck : ShieldCheck;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => toggle(r.id)}
                        className={cn(
                          'w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                          checked ? 'border-[#4A3B32] bg-[#FCFAF7]' : 'border-[#E6E0D9] hover:bg-[#FCFAF7]'
                        )}
                        data-testid={`send-doc-recipient-${r.id}`}
                      >
                        <span className={cn('w-4 h-4 rounded border flex items-center justify-center', checked ? 'bg-[#4A3B32] border-[#4A3B32]' : 'border-[#D5CFC7]')}>
                          {checked && <span className="w-2 h-2 bg-white rounded-sm" />}
                        </span>
                        <Icon className="w-4 h-4 text-[#8C8078] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#2C2420] truncate">{r.name}</div>
                          <div className="text-xs text-[#8C8078] truncate">
                            {r.email} <span className="text-[#A86A3A]">· {ROLE_LABEL[r.portalRole] || r.portalRole}</span>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Note */}
          <div>
            <Label htmlFor="send-doc-note" className="text-[#2C2420]">Personal note <span className="text-[#8C8078]">(optional)</span></Label>
            <textarea
              id="send-doc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Quick context to include in the email…"
              className="mt-1 w-full rounded-lg border border-[#E6E0D9] px-3 py-2 text-sm text-[#2C2420] focus:outline-none focus:border-[#4A3B32]"
              data-testid="send-doc-note"
            />
          </div>

          {/* Info banner */}
          <div className="rounded-lg bg-[#FCFAF7] border border-[#F0EAE3] p-3 flex items-start gap-2">
            <Mail className="w-4 h-4 text-[#A86A3A] mt-0.5 shrink-0" />
            <p className="text-xs text-[#5D524D] leading-relaxed">
              Each recipient gets a personalised email with a one-click sign-in to your portal that's valid for 14 days. They land on Documents and can open this file.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#F0EAE3] flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="send-doc-cancel-btn">Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || loading || selected.size === 0}
            className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white"
            data-testid="send-doc-confirm-btn"
          >
            {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : (<><Send className="w-4 h-4 mr-2" />Send {selected.size > 0 ? `to ${selected.size}` : ''}</>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
